
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PersonalInfo from './components/PersonalInfo';
import ContactInfo from './components/ContactInfo';
import PurchaseInfo from './components/PurchaseInfo';
import InterestInfo from './components/InterestInfo';
import ReferenceSource from './components/ReferenceSource';
import SignatureSection from './components/SignatureSection';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/base/ToastContainer';

interface FormData {
  // Kişisel Bilgiler
  firstName: string;
  lastName: string;
  gender: string;
  birthDate: string;
  maritalStatus: string;
  childrenCount: string;
  childrenAge: string;
  occupation: string;
  company: string;
  // İletişim Bilgileri
  phone: string;
  email: string;
  district: string;
  city: string;
  // Konut Satın Alma
  purchaseReason: string;
  housingType: string;
  // Referans
  referenceSource: string;
  referralName: string;
  // İmza
  signature: string;
  kvkkAccepted: string;
}

export default function HomePage() {
  const navigate = useNavigate();
  const [formUser, setFormUser] = useState<any>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    gender: '',
    birthDate: '',
    maritalStatus: '',
    childrenCount: '',
    childrenAge: '',
    occupation: '',
    company: '',
    phone: '',
    email: '',
    district: '',
    city: '',
    purchaseReason: '',
    housingType: '',
    referenceSource: '',
    referralName: '',
    signature: '',
    kvkkAccepted: ''
  });
  const [signature, setSignature] = useState('');
  const [salesRepresentative, setSalesRepresentative] = useState('');
  const [kvkkConsent, setKvkkConsent] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const toast = useToast();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    const user = localStorage.getItem('form_user');
    if (!user) {
      setIsCheckingAuth(false);
      navigate('/form-login', { replace: true });
      return;
    }
    
    try {
      const parsedUser = JSON.parse(user);
      setFormUser(parsedUser);
    } catch (error) {
      console.error('User parse error:', error);
      localStorage.removeItem('form_user');
      navigate('/form-login', { replace: true });
      return;
    }
    
    setIsCheckingAuth(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('form_user');
    toast.info('Çıkış yapıldı');
    navigate('/form-login', { replace: true });
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClearForm = () => {
    if (window.confirm('Formu temizlemek istediğinizden emin misiniz? Tüm girilen bilgiler silinecektir.')) {
      setFormData({
        firstName: '',
        lastName: '',
        gender: '',
        birthDate: '',
        maritalStatus: '',
        childrenCount: '',
        childrenAge: '',
        occupation: '',
        company: '',
        phone: '',
        email: '',
        district: '',
        city: '',
        purchaseReason: '',
        housingType: '',
        referenceSource: '',
        referralName: '',
        signature: '',
        kvkkAccepted: ''
      });
      setSignature('');
      setSalesRepresentative('');
      setKvkkConsent(false);
      
      // Trigger canvas clear event
      window.dispatchEvent(new CustomEvent('clearSignature'));
      // Trigger housing types clear event
      window.dispatchEvent(new CustomEvent('clearHousingTypes'));
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.phone || !formData.email) {
      toast.warning('Lütfen zorunlu alanları doldurun (İsim, Soyisim, Telefon, E-posta)');
      return;
    }

    if (!formData.housingType || formData.housingType === '[]') {
      toast.warning('Lütfen en az bir konut tipi seçin');
      return;
    }

    if (!signature) {
      toast.warning('Lütfen formu imzalayın');
      return;
    }

    if (!kvkkConsent) {
      toast.warning('Lütfen KVKK Aydınlatma Metni\'ni okuyup onaylayın');
      return;
    }

    if (!salesRepresentative) {
      toast.warning('Lütfen satış temsilcisi seçiniz');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Parse housing types
      let housingTypeArray: string[] = [];
      try {
        housingTypeArray = JSON.parse(formData.housingType);
      } catch {
        housingTypeArray = [];
      }

      // Supabase'e kaydet
      const { error: supabaseError } = await supabase
        .from('customer_forms')
        .insert([
          {
            first_name: formData.firstName,
            last_name: formData.lastName,
            gender: formData.gender,
            birth_date: formData.birthDate,
            marital_status: formData.maritalStatus,
            children_count: formData.childrenCount,
            children_age: formData.childrenAge,
            occupation: formData.occupation,
            company: formData.company,
            phone: formData.phone,
            email: formData.email,
            city: formData.city,
            district: formData.district,
            purchase_reason: formData.purchaseReason,
            housing_type: housingTypeArray,
            reference_source: formData.referenceSource,
            reference_name: formData.referralName || null,
            signature: signature,
            kvkk_consent: kvkkConsent,
            sales_representative: salesRepresentative
          }
        ]);

      if (supabaseError) throw supabaseError;

      // Form API'ye de gönder
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      formDataToSend.append('signature', signature);
      formDataToSend.append('kvkkConsent', kvkkConsent.toString());
      formDataToSend.append('salesRepresentative', salesRepresentative);

      await fetch('https://readdy.ai/api/form/d5osth0skvai0ssanr20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      setSubmitStatus('success');
      
      // 5 saniye sonra popup'ı kapat ve formu temizle
      setTimeout(() => {
        setSubmitStatus('idle');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          gender: '',
          birthDate: '',
          maritalStatus: '',
          childrenCount: '',
          childrenAge: '',
          occupation: '',
          company: '',
          phone: '',
          email: '',
          district: '',
          city: '',
          purchaseReason: '',
          housingType: '',
          referenceSource: '',
          referralName: '',
          signature: '',
          kvkkAccepted: ''
        });
        setSignature('');
        setSalesRepresentative('');
        setKvkkConsent(false);
        
        // Trigger canvas clear event
        window.dispatchEvent(new CustomEvent('clearSignature'));
        // Trigger housing types clear event
        window.dispatchEvent(new CustomEvent('clearHousingTypes'));
        
        // Sayfayı en üste kaydır
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 5000);
    } catch (error) {
      console.error('Form gönderilirken hata:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auth kontrolü devam ediyorsa loading göster
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-slate-600 animate-spin"></i>
          <p className="mt-4 text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Kullanıcı yoksa hiçbir şey render etme
  if (!formUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/31fbc47984442cd67e6272bbd120d3e0.png" 
              alt="Logo" 
              className="h-12 object-contain"
            />
            <h1 className="text-xl font-semibold text-slate-800">Misafir Bilgi Formu</h1>
          </div>
          <div className="flex items-center gap-4">
            {formUser && (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-800">{formUser.full_name}</p>
                  <p className="text-xs text-slate-500">{formUser.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-logout-box-line"></i>
                  Çıkış
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Form */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <form 
          id="customer-form" 
          data-readdy-form 
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Kişisel Bilgiler */}
          <PersonalInfo formData={formData} updateFormData={updateFormData} />

          {/* İletişim Bilgileri */}
          <ContactInfo formData={formData} updateFormData={updateFormData} />

          {/* Konut Satın Alma */}
          <PurchaseInfo formData={formData} updateFormData={updateFormData} />

          {/* İlgi Alanları */}
          <InterestInfo formData={formData} updateFormData={updateFormData} />

          {/* Referans Kaynağı */}
          <ReferenceSource formData={formData} updateFormData={updateFormData} />

          {/* İmza Bölümü */}
          <SignatureSection 
            onSignatureChange={setSignature} 
            onKvkkChange={setKvkkConsent}
            onSalesRepChange={setSalesRepresentative}
          />

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={handleClearForm}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-4 px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-eraser-line text-lg"></i>
                Formu Temizle
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line text-lg animate-spin"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-lg"></i>
                    Formu Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </main>

      {/* Success Popup Modal */}
      {submitStatus === 'success' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-scaleIn">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-5xl text-emerald-600"></i>
            </div>
            
            {/* Success Message */}
            <h3 className="text-2xl font-bold text-slate-800 mb-3">
              Form Başarıyla Gönderildi!
            </h3>
            <p className="text-slate-600 mb-6">
              Bilgileriniz başarıyla kaydedildi. Kısa süre içinde sizinle iletişime geçeceğiz.
            </p>
            
            {/* Progress Bar */}
            <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 animate-progressBar"></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Bu pencere 5 saniye içinde otomatik kapanacak...
            </p>
          </div>
        </div>
      )}

      {/* Error Popup Modal */}
      {submitStatus === 'error' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            {/* Error Icon */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-close-circle-fill text-5xl text-red-600"></i>
            </div>
            
            {/* Error Message */}
            <h3 className="text-2xl font-bold text-slate-800 mb-3">
              Bir Hata Oluştu
            </h3>
            <p className="text-slate-600 mb-6">
              Form gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.
            </p>
            
            {/* Close Button */}
            <button
              onClick={() => setSubmitStatus('idle')}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold whitespace-nowrap cursor-pointer"
            >
              Kapat
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
